import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ShieldCheck,
  Sparkles,
  Briefcase,
  Clock,
  CheckCircle2,
  FileCheck2,
  Users2,
  Stethoscope,
  Star,
  ChevronRight,
} from 'lucide-react';
import { ButtonLink } from '@/components/ui/ButtonLink';
import { Badge } from '@/components/ui/Badge';
import { SEO } from '@/components/common/SEO';
import { mockJobs } from '@/mock/jobs';
import { jobService } from '@/services/jobService';
import { formatRate, formatDate } from '@/lib/format';
import type { Job } from '@/types';

export function HomePage() {
  return (
    <div className="overflow-hidden">
      <SEO
        title="Locum doctor recruitment & compliance — Australia"
        description="Premium locum doctor recruitment with real-time AHPRA verification, AI-classified compliance, and credentialed candidates sent direct to hospitals. Australian-owned."
        canonical="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Locum Network',
          url: 'https://locumnetwork.com.au',
          areaServed: 'AU',
          description: 'Locum doctor recruitment and compliance platform for Australian hospitals and medical practitioners.',
        }}
      />
      <Hero />
      <TrustStrip />
      <DualCTA />
      <FeaturedJobs />
      <HowItWorks />
      <ComplianceFeature />
      <Testimonials />
      <FAQ />
      <FinalCTA />
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────── */

function Hero() {
  return (
    <section className="relative">
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-soft/40 via-transparent to-transparent" />
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-primary/15 blur-3xl" />
        <div className="absolute top-32 -left-20 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
      </div>
      <div className="container-page pt-16 pb-20 lg:pt-24 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Badge tone="primary" icon={<Sparkles className="h-3 w-3" />}>
              AHPRA-aware compliance, built for Australia
            </Badge>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-ink leading-[1.05]">
              The locum platform Australian doctors{' '}
              <span className="bg-gradient-to-r from-primary to-success bg-clip-text text-transparent">
                actually trust.
              </span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-ink-muted leading-relaxed">
              From AHPRA verification to placement in days — Locum Network combines premium
              recruitment with an honest, transparent compliance engine. Doctors finish onboarding
              in under five minutes. Hospitals get credentialed candidates without the back-and-forth.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <ButtonLink to="/register" size="lg" iconRight={<ArrowRight className="h-4 w-4" />}>
                Join as a doctor
              </ButtonLink>
              <ButtonLink to="/for-clinics" size="lg" variant="outline">
                For clinics & hospitals
              </ButtonLink>
            </div>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm text-ink-muted">
              {[
                'Bulk doc upload, AI-classified',
                'AHPRA + WWCC + visa coverage',
                'Real-time placement updates',
              ].map((t) => (
                <li key={t} className="inline-flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5">
            <HeroCard />
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroCard() {
  return (
    <div className="relative">
      <div className="absolute -inset-6 -z-10 rounded-[2rem] bg-gradient-to-br from-primary/10 via-accent/10 to-transparent blur-2xl" />
      <div className="surface-elevated p-6 backdrop-blur">
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-xs uppercase tracking-wider text-ink-subtle">Onboarding</p>
            <p className="font-display text-lg font-semibold text-ink">Compliance progress</p>
          </div>
          <Badge tone="success" icon={<CheckCircle2 className="h-3 w-3" />}>92%</Badge>
        </div>
        <ul className="space-y-3">
          {[
            { label: 'AHPRA registration', value: 'Verified', tone: 'success' as const },
            { label: 'Passport & ID', value: 'Verified', tone: 'success' as const },
            { label: 'Police check (renewal)', value: 'Pending review', tone: 'warning' as const },
            { label: 'Indemnity insurance', value: 'Auto-classified', tone: 'info' as const },
            { label: 'Vaccination record', value: 'Uploaded', tone: 'info' as const },
          ].map((row) => (
            <li
              key={row.label}
              className="flex items-center justify-between rounded-lg border border-border bg-bg/40 px-3 py-2.5"
            >
              <div className="flex items-center gap-2.5">
                <FileCheck2 className="h-4 w-4 text-ink-muted" />
                <span className="text-sm text-ink">{row.label}</span>
              </div>
              <Badge tone={row.tone}>{row.value}</Badge>
            </li>
          ))}
        </ul>
        <div className="mt-5 rounded-xl bg-primary-soft p-4">
          <p className="text-xs font-semibold text-primary">Next best action</p>
          <p className="mt-1 text-sm text-ink">
            Upload a current Working With Children Check to be ready for QLD placements.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────── */

function TrustStrip() {
  const stats = [
    { label: 'Doctors on platform', value: '4,200+' },
    { label: 'Average placement', value: '5.2 days' },
    { label: 'Compliance verified', value: '99.4%' },
    { label: 'Hospitals served', value: '180+' },
  ];
  return (
    <section className="border-y border-border bg-surface/60">
      <div className="container-page py-8 grid grid-cols-2 sm:grid-cols-4 gap-6">
        {stats.map((s) => (
          <div key={s.label} className="text-center">
            <p className="font-display text-2xl font-bold text-ink">{s.value}</p>
            <p className="mt-1 text-xs uppercase tracking-wider text-ink-subtle">{s.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function DualCTA() {
  return (
    <section className="container-page py-20">
      <div className="grid lg:grid-cols-2 gap-6">
        <CTACard
          eyebrow="Doctors"
          title="Get placed in days, not months"
          description="Build your profile once. Apply to jobs in two clicks. Watch your compliance score climb as our AI classifies the documents you upload."
          ctaLabel="Create your profile"
          ctaTo="/register"
          features={['CV manager + AI polish', 'AHPRA, WWCC, visa tracking', 'Transparent rates']}
          tone="primary"
          Icon={Stethoscope}
        />
        <CTACard
          eyebrow="Clinics & hospitals"
          title="Credentialed candidates, sent to you"
          description="Tell us what you need. We send you compliance-verified, briefed candidates with rate, availability and a one-line summary — ready to interview."
          ctaLabel="Speak to our recruitment team"
          ctaTo="/for-clinics"
          features={['Compliance-first sourcing', 'Single point of contact', 'Australian-only network']}
          tone="ink"
          Icon={Briefcase}
        />
      </div>
    </section>
  );
}

function CTACard({
  eyebrow,
  title,
  description,
  ctaLabel,
  ctaTo,
  features,
  tone,
  Icon,
}: {
  eyebrow: string;
  title: string;
  description: string;
  ctaLabel: string;
  ctaTo: string;
  features: string[];
  tone: 'primary' | 'ink';
  Icon: typeof Stethoscope;
}) {
  const isInk = tone === 'ink';
  return (
    <div
      className={
        isInk
          ? 'relative overflow-hidden rounded-2xl bg-ink p-8 text-bg shadow-lift'
          : 'relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-primary-hover p-8 text-primary-ink shadow-lift'
      }
    >
      <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" aria-hidden />
      <div className="relative">
        <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
          <Icon className="h-3.5 w-3.5" />
          {eyebrow}
        </div>
        <h3 className="mt-5 font-display text-2xl sm:text-3xl font-bold tracking-tight">{title}</h3>
        <p className="mt-3 max-w-md text-sm sm:text-base opacity-90">{description}</p>
        <ul className="mt-5 space-y-2 text-sm">
          {features.map((f) => (
            <li key={f} className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 opacity-80" /> {f}
            </li>
          ))}
        </ul>
        <Link
          to={ctaTo}
          className="mt-7 inline-flex items-center gap-1.5 rounded-lg bg-white text-ink px-4 py-2.5 text-sm font-semibold hover:bg-white/90"
        >
          {ctaLabel}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────── */

function FeaturedJobs() {
  // Pull live active jobs in real mode; fall back to mocks if Supabase
  // isn't configured. Limit to 4 for the featured strip.
  const [featured, setFeatured] = useState<Job[]>(mockJobs.slice(0, 4));
  useEffect(() => {
    let cancelled = false;
    jobService.list({ status: 'active' })
      .then((all) => { if (!cancelled) setFeatured(all.slice(0, 4)); })
      .catch(() => { /* keep mock fallback on any error */ });
    return () => { cancelled = true; };
  }, []);
  return (
    <section className="container-page py-20">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">Live placements</p>
          <h2 className="mt-1 font-display text-3xl font-bold tracking-tight text-ink">Featured jobs</h2>
        </div>
        <Link to="/jobs" className="link inline-flex items-center gap-1 text-sm">
          Browse all jobs <ChevronRight className="h-3.5 w-3.5" />
        </Link>
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {featured.map((j) => (
          <Link
            key={j.id}
            to={`/jobs/${j.id}`}
            className="group surface flex flex-col p-5 transition hover:-translate-y-0.5 hover:shadow-card"
          >
            <Badge tone="primary" size="sm">{j.specialty}</Badge>
            <h3 className="mt-3 font-display text-base font-semibold text-ink group-hover:text-primary">
              {j.title}
            </h3>
            <p className="mt-1 text-sm text-ink-muted">{j.hospital.name} · {j.city}, {j.state}</p>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="font-display text-xl font-bold text-ink">{formatRate(j.rateAmount, j.rateType)}</span>
            </div>
            <div className="mt-2 flex items-center gap-3 text-xs text-ink-muted">
              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />Starts {formatDate(j.startDate)}</span>
            </div>
            <div className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-primary">
              View placement <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ───────────────────────────────────────────────────────────────────── */

function HowItWorks() {
  const steps = [
    {
      n: 1,
      title: 'Sign up & upload',
      body: 'Create your profile in under two minutes. Bulk-upload your documents — our AI classifies AHPRA, indemnity, police checks and more.',
      Icon: FileCheck2,
    },
    {
      n: 2,
      title: 'We verify & match',
      body: 'A real compliance specialist reviews flagged items. Your profile is matched to live placements that fit your specialty, location and availability.',
      Icon: ShieldCheck,
    },
    {
      n: 3,
      title: 'Get the offer',
      body: 'We present you to hospitals with rate, availability and a clear summary. Accept the offer in your portal — no email tag.',
      Icon: Briefcase,
    },
  ];
  return (
    <section className="bg-surface border-y border-border">
      <div className="container-page py-20">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">How it works</p>
          <h2 className="mt-1 font-display text-3xl font-bold tracking-tight text-ink">Three steps. Zero faff.</h2>
          <p className="mt-3 text-base text-ink-muted">
            Designed by clinicians and recruiters who got tired of half-built portals and email chains.
          </p>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {steps.map((s) => (
            <div key={s.n} className="surface p-6">
              <div className="flex items-center justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-soft text-primary">
                  <s.Icon className="h-5 w-5" />
                </div>
                <span className="font-display text-3xl font-bold text-ink/10">0{s.n}</span>
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold text-ink">{s.title}</h3>
              <p className="mt-2 text-sm text-ink-muted leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────────────────────────────────────────────────── */

function ComplianceFeature() {
  return (
    <section className="container-page py-24">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">Compliance Locker</p>
          <h2 className="mt-1 font-display text-3xl sm:text-4xl font-bold tracking-tight text-ink">
            The end of compliance dread.
          </h2>
          <p className="mt-4 text-base text-ink-muted leading-relaxed">
            One screen. Bulk drag-and-drop. Documents are classified, dates extracted, expiries
            tracked. We tell you exactly what's missing — and what's ready to send to a hospital today.
          </p>
          <ul className="mt-6 space-y-3">
            {[
              { t: 'AI document classification with confidence scores' },
              { t: 'Visa, AHPRA, WWCC, indemnity, police check tracking' },
              { t: 'Expiry alerts before they break a placement' },
              { t: 'Replace, version, and re-submit in one click' },
            ].map((f) => (
              <li key={f.t} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary">
                  <CheckCircle2 className="h-3 w-3" />
                </span>
                <span className="text-sm text-ink">{f.t}</span>
              </li>
            ))}
          </ul>
          <div className="mt-7">
            <ButtonLink to="/register" size="lg">
              Try the compliance locker
            </ButtonLink>
          </div>
        </div>
        <div className="relative">
          <div className="absolute -inset-8 -z-10 rounded-[2rem] bg-gradient-to-br from-primary-soft via-transparent to-accent-soft blur-2xl" />
          <div className="surface-elevated p-6">
            <div className="flex items-center gap-3 border-b border-border pb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-ink">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <p className="font-display text-base font-semibold text-ink">Sarah Chen — Compliance score</p>
                <p className="text-xs text-ink-muted">Updated 2 minutes ago · Reviewed by Mark Dunlop</p>
              </div>
              <span className="ml-auto font-display text-2xl font-bold text-ink tabular-nums">78%</span>
            </div>
            <div className="mt-4 space-y-2.5">
              {[
                ['AHPRA', 'verified', 'success'],
                ['Police check', 'expiring 28d', 'warning'],
                ['Indemnity', 'rejected', 'danger'],
                ['Vaccination', 'in review', 'info'],
                ['Visa', 'missing', 'neutral'],
              ].map(([n, s, t]) => (
                <div key={n as string} className="flex items-center justify-between rounded-lg bg-bg/40 px-3 py-2.5">
                  <span className="text-sm text-ink">{n}</span>
                  <Badge tone={t as 'success'}>{s}</Badge>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────────────────────────────────────────────────── */

function Testimonials() {
  const items = [
    {
      quote:
        '"Onboarded, compliance verified, and on the floor at a tertiary hospital in eight days. The compliance locker is the most polished I have seen on any locum platform."',
      name: 'Dr A. Patel',
      role: 'Anaesthetics Registrar, Melbourne',
    },
    {
      quote:
        '"Everything\'s in one place — referees, contracts, offers. I have stopped using a spreadsheet to track my own credentialling."',
      name: 'Dr H. Nguyen',
      role: 'GP, Sydney',
    },
    {
      quote:
        '"From posting a role to a credentialed candidate landing in our inbox: under 36 hours. Game changer for our weekend coverage."',
      name: 'Sam Fields',
      role: 'Workforce Manager, regional QLD hospital',
    },
  ];
  return (
    <section className="bg-surface border-y border-border">
      <div className="container-page py-20">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">Loved by doctors</p>
          <h2 className="mt-1 font-display text-3xl font-bold tracking-tight text-ink">
            What our network is saying
          </h2>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {items.map((t, i) => (
            <figure key={i} className="surface p-6 flex flex-col">
              <div className="flex gap-0.5 text-accent">
                {Array.from({ length: 5 }).map((_, k) => (
                  <Star key={k} className="h-4 w-4 fill-current" />
                ))}
              </div>
              <blockquote className="mt-3 text-sm text-ink leading-relaxed">{t.quote}</blockquote>
              <figcaption className="mt-5 pt-4 border-t border-border">
                <p className="text-sm font-semibold text-ink">{t.name}</p>
                <p className="text-xs text-ink-muted">{t.role}</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────────────────────────────────────────────────── */

function FAQ() {
  const items = [
    {
      q: 'Are you Australian only?',
      a: 'Yes. Locum Network is Australian-owned and built around AHPRA, WWCC, and Australian visa categories. We do not place doctors outside of Australia.',
    },
    {
      q: 'Do I need to upload all my documents at once?',
      a: 'No. You can sign up in two minutes and progressively upload as you go. Most doctors complete onboarding in under 5 minutes using bulk upload.',
    },
    {
      q: 'Who reviews my compliance documents?',
      a: 'AI classifies and pre-verifies on upload, then a qualified compliance specialist on our team performs the final review. We do not auto-approve documents.',
    },
    {
      q: 'How are you different from agency XYZ?',
      a: 'We treat compliance as the product, not paperwork. Doctors get a transparent score, expiry alerts, and version control. Hospitals get credentialed candidates, not just CVs.',
    },
  ];
  return (
    <section className="container-page py-20">
      <div className="grid lg:grid-cols-3 gap-10">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">FAQ</p>
          <h2 className="mt-1 font-display text-3xl font-bold tracking-tight text-ink">Common questions</h2>
          <p className="mt-3 text-sm text-ink-muted">Got something else? <Link to="/contact" className="link">Get in touch</Link>.</p>
        </div>
        <div className="lg:col-span-2 space-y-3">
          {items.map((f) => (
            <details key={f.q} className="surface group p-5">
              <summary className="flex cursor-pointer items-center justify-between gap-4 list-none">
                <span className="font-display text-base font-semibold text-ink">{f.q}</span>
                <ChevronRight className="h-4 w-4 text-ink-muted transition group-open:rotate-90" />
              </summary>
              <p className="mt-3 text-sm text-ink-muted leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="container-page pb-24">
      <div className="surface-elevated relative overflow-hidden bg-gradient-to-br from-ink to-ink/95 p-10 sm:p-14 text-bg">
        <div className="absolute inset-0 -z-10 opacity-20">
          <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-primary/40 blur-3xl" />
          <div className="absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-accent/30 blur-3xl" />
        </div>
        <div className="grid lg:grid-cols-3 gap-8 items-center">
          <div className="lg:col-span-2">
            <Badge tone="accent" icon={<Users2 className="h-3 w-3" />}>Join 4,200+ Australian doctors</Badge>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-bg">
              Your next placement, with the paperwork already done.
            </h2>
            <p className="mt-3 max-w-lg text-bg/80">
              Sign up free. No commitment, no exclusivity. Just better tooling for the work you already do.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
            <ButtonLink to="/register" size="lg" iconRight={<ArrowRight className="h-4 w-4" />}>
              Create your profile
            </ButtonLink>
            <Link
              to="/jobs"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/20 px-5 py-2.5 text-sm font-medium text-bg hover:bg-white/10"
            >
              Browse live jobs
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
